import React from 'react';
import { Upload, FileText, MoreVertical, Download, Trash2, Edit } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { downloadDocumentFile } from '../utils/downloadDocument';

const DocumentsTab = ({
    documents,
    documentMenuOpen,
    setDocumentMenuOpen,
    deleteDocument,
    fileInputRef,
    handleFileUpload,
    renamingDocument,
    setRenamingDocument,
    renameDocument,
    projects = [],
    onAssignItemProject
}) => {
    const menuRefs = React.useRef({});

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            let clickedInsideMenu = false;
            Object.values(menuRefs.current).forEach(ref => {
                if (ref && ref.contains(event.target)) {
                    clickedInsideMenu = true;
                }
            });
            
            if (!clickedInsideMenu) {
                setDocumentMenuOpen(null);
            }
        };

        if (documentMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [documentMenuOpen, setDocumentMenuOpen]);

    const handleDownload = async (docItem) => {
        setDocumentMenuOpen(null);
        let payload = docItem;
        const hasText =
            (docItem?.fullText && String(docItem.fullText).trim()) ||
            (docItem?.content && String(docItem.content).trim());
        if (!hasText && docItem?.id) {
            try {
                const snap = await getDoc(doc(db, 'documents', String(docItem.id)));
                if (snap.exists()) {
                    payload = { ...docItem, ...snap.data(), id: snap.id };
                }
            } catch (err) {
                console.error('Failed to load document for download:', err);
            }
        }
        downloadDocumentFile(payload);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
                <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl hover:from-purple-700 hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                >
                    <Upload className="w-5 h-5 mr-2" />
                    Upload Documents
                </button>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx"
            />

            <div className="grid gap-4">
                {documents.map(doc => (
                    <div key={doc.id} className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300 relative">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center">
                                    <FileText className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    {renamingDocument === doc.id ? (
                                        <input
                                            type="text"
                                            defaultValue={doc.name}
                                            className="font-semibold text-gray-900 text-lg bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    renameDocument(doc.id, e.target.value);
                                                } else if (e.key === 'Escape') {
                                                    setRenamingDocument(null);
                                                }
                                            }}
                                            onBlur={(e) => renameDocument(doc.id, e.target.value)}
                                            autoFocus
                                        />
                                    ) : (
                                        <h3 className="font-semibold text-gray-900 text-lg">{doc.name}</h3>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-1">
                                        <span className="bg-gray-100/80 px-2 py-1 rounded-lg">{(doc.size / 1024).toFixed(1)} KB</span>
                                        <span>•</span>
                                        <span>Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</span>
                                        {onAssignItemProject && projects.length > 0 && (
                                            <label className="inline-flex items-center gap-1 text-xs ml-1">
                                                <span className="text-gray-500">Project</span>
                                                <select
                                                    className="border border-gray-200 rounded-lg px-2 py-0.5 bg-white text-gray-800 max-w-[10rem]"
                                                    value={doc.projectId || ''}
                                                    onChange={(e) =>
                                                        onAssignItemProject('document', doc.id, e.target.value || 'unassigned')
                                                    }
                                                >
                                                    <option value="">None</option>
                                                    {projects.map((p) => (
                                                        <option key={p.id} value={p.id}>
                                                            {p.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="px-3 py-1 bg-purple-100/80 text-purple-700 rounded-lg text-xs font-medium border border-purple-200/50">
                                    {doc.type?.split('/')[1]?.toUpperCase() || 'FILE'}
                                </span>
                                <div className="relative z-10" ref={el => menuRefs.current[doc.id] = el}>
                                    <button
                                        onClick={() => setDocumentMenuOpen(documentMenuOpen === doc.id ? null : doc.id)}
                                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    >
                                        <MoreVertical className="w-4 h-4 text-gray-500" />
                                    </button>

                                    {documentMenuOpen === doc.id && (
                                        <div className="absolute right-0 bottom-4 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999] min-w-[140px] max-h-48 overflow-y-auto">
                                            <button
                                                type="button"
                                                onClick={() => handleDownload(doc)}
                                                className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                            >
                                                <Download className="w-3 h-3 mr-2" />
                                                Download
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setRenamingDocument(doc.id);
                                                }}
                                                className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center"
                                            >
                                                <Edit className="w-3 h-3 mr-2" />
                                                Rename
                                            </button>
                                            <button
                                                onClick={() => deleteDocument(doc.id)}
                                                className="w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs flex items-center text-red-600"
                                            >
                                                <Trash2 className="w-3 h-3 mr-2" />
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {documents.length === 0 && (
                <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Upload className="w-12 h-12 text-purple-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">No documents uploaded yet</h3>
                    <p className="text-gray-600 mb-8">Upload documents to enhance your AI knowledge base</p>
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl hover:from-purple-700 hover:to-violet-700 transition-all duration-200 font-medium shadow-lg text-lg"
                    >
                        <Upload className="w-6 h-6 mr-3" />
                        Upload Your First Document
                    </button>
                </div>
            )}
        </div>
    );
};

export default DocumentsTab;